import { Link } from "react-router-dom";
import { Scale, FileText } from "lucide-react";
import PageMeta from "./PageMeta";

export default function ScalesPolicyPage() {
  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Weighing Scales Policy | Redruth Sea Angling Association"
        description="Redruth Sea Angling Association's weighing scales policy and scale-check requirements for competition catches."
        keywords="RSAA scales policy, sea angling weighing rules, Redruth Sea Angling Association scales"
        url="https://www.redruthseaanglingassociation.co.uk/scales-policy"
      />

      {/* Full image — contained so nothing is cropped */}
      <div className="relative bg-slate-950">
        <img
          src="/weigh_scales.png"
          alt="Weighing scales"
          className="mx-auto w-full object-contain"
          style={{ maxHeight: '80vh' }}
        />
        {/* Fade bottom of image into the card */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-950 to-transparent" />
      </div>

      {/* Content card pulled up to overlap the image */}
      <div className="relative z-10 -mt-32 mx-auto max-w-3xl px-6 pb-16 sm:px-8 lg:px-12">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/10 backdrop-blur">
            <Scale className="h-6 w-6 text-cyan-300" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Club Policy</p>
            <h1 className="heading-glow mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">Weighing Scales Policy Notice</h1>
          </div>
        </div>

        <div className="mt-8 space-y-8 rounded-[2rem] border border-white/10 bg-slate-900/95 p-8 backdrop-blur panel-lift">

          <p className="text-base leading-7 text-slate-300">
            The Committee has agreed to adopt a scales policy. This policy is intended to ensure consistency,
            fairness, and accuracy in the weighing and recording of fish.
          </p>

          <section>
            <h2 className="text-xl font-semibold text-white">Implementation of Scale Checks</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              All members' weighing scales will be required to undergo an accuracy check prior to the
              commencement of the 2027 season.
            </p>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              To support this requirement, scale testing will be made available at the November and December
              meetings, with a further opportunity to be provided at a social evening, should one be arranged.
            </p>
          </section>

          <section className="rounded-xl border border-cyan-300/20 bg-cyan-400/5 p-6 panel-lift">
            <h2 className="text-xl font-semibold text-white">Requirement from 2027</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              With effect from 1 January 2027, fish will only be accepted for recording if they have been
              weighed using scales that have been checked and approved in accordance with club policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white">Arrangements for the Remainder of the Current Year</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              For the remainder of the current year, members are strongly encouraged to present their scales
              for testing to ensure compliance ahead of 2027.
            </p>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              Where a fish is weighed using scales that have not yet been officially checked, those scales must
              subsequently be tested and confirmed as accurate before the fish can be accepted by the Club Recorder.
            </p>
          </section>

          <a
            href="/assets/rsaa_weighing_scales_policy.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 rounded-xl bg-cyan-500 px-6 py-5 text-slate-950 shadow-lg transition hover:bg-cyan-400 active:scale-95"
          >
            <FileText className="h-6 w-6 shrink-0" />
            <span className="text-base font-bold">Download Weighing Scales Policy (PDF)</span>
          </a>

        </div>
      </div>

    </div>
  );
}
